import { useTranslation } from "react-i18next"

import type { CapabilityMode } from "@/api/admin/delegation"

import { Gauge } from "@/components/icons"

import {
  FairUseConfigConflictBanner,
  FairUseConfigFormProvider,
} from "./fair-use-config-form-provider"
import { FairUseTabDrift } from "./fair-use-tab-drift"
import { FairUseTabPlans } from "./fair-use-tab-plans"
import { FairUseTabSettings } from "./fair-use-tab-settings"
import { FairUseTabCosts, FairUseTabLeads, FairUseTabUsers } from "./fair-use-tab-users"
import { AdminSubNavigation } from "../admin-sub-navigation"
import { LockedManagementSection } from "../locked-management-section"

/**
 * The views of the Fair use area, in tab order. Structurally the same union as
 * `FairUseViewId` in `admin-dashboard-route-state.ts`, which is what the page
 * resolves `?view=` into; it is restated here so this area does not depend on
 * the route module to render.
 */
export type FairUseAreaView = "drift" | "users" | "leads" | "costs" | "plans" | "settings"

type FairUseAreaProps = {
  view: FairUseAreaView
  onViewChange: (view: FairUseAreaView) => void
  showNavigation?: boolean
  /**
   * The RESOLVED `fair_use` mode for whoever the workspace is currently
   * showing — the real `/api/config` value, or the previewed customer's
   * delegated mode while a platform admin has "preview as customer admin" on.
   * Undefined while that is not yet known, which fails closed.
   */
  mode: CapabilityMode | undefined
  /**
   * Resolved the same way: false throughout a customer preview, however real
   * the viewer's platform-admin rights are. It decides what the area shows,
   * not what the backend will accept — the routes gate on the real identity.
   */
  isPlatformAdmin: boolean
}

/**
 * Fair use, as one admin area.
 *
 * It used to be four places: the AI-budget card in Usage & cost, the limits and
 * plans section in Configuration, the lead roster in another Configuration
 * section, and the lead allowance on a third panel. Plans were defined in one
 * and assigned in another. They are now four tabs over one capability
 * (`fair_use`) and — for everything that configures — one document.
 *
 * The capability gate is the same one the backend routes use, and the same one
 * management sections use: platform admins always, customer admins with
 * `manage`. `available` (delegable, not yet granted) renders the locked
 * preview rather than the real area — mounting the real one would fire its
 * data hooks for a surface the customer cannot use yet — and `hidden` renders
 * nothing at all.
 *
 * Both inputs are RESOLVED BY THE PAGE and passed in, never re-derived from
 * `/api/config` here. The page is the only place that knows whether a
 * platform admin is previewing as a customer admin, and it already resolves
 * the same two values for the navigation
 * (`getVisibleAdminAreas`). Re-deriving them would make this area the one
 * surface in the workspace that a preview cannot reach — it would answer
 * "platform admin, everything" whatever the preview said.
 *
 * The form provider sits ABOVE the tab switch: Plans & pricing, Settings and
 * the lead allowance on Users & licences write one document with one hash, and
 * a tab that owned its own copy would both lose unsaved edits on a switch and
 * send a stale `expectedHash`.
 */
export function FairUseArea({
  view,
  onViewChange,
  mode,
  isPlatformAdmin,
  showNavigation = true,
}: FairUseAreaProps) {
  const { t } = useTranslation()

  if (!isPlatformAdmin && mode !== "manage") {
    if (mode === "available") {
      return (
        <LockedManagementSection
          icon={<Gauge className="size-5" />}
          title={t("adminDashboard.fairUse.settings.title", "Fair use")}
        />
      )
    }
    return null
  }

  const options = [
    {
      id: "drift",
      label: t("adminDashboard.workspaceNavigation.fairUse.drift", "Operations"),
    },
    {
      id: "users",
      label: t("adminDashboard.workspaceNavigation.fairUse.users", "Users & licences"),
    },
    {
      id: "plans",
      label: t("adminDashboard.workspaceNavigation.fairUse.plans", "Plans & pricing"),
    },
    {
      id: "settings",
      label: t("adminDashboard.workspaceNavigation.fairUse.settings", "Settings"),
    },
  ]

  return (
    <FairUseConfigFormProvider isPlatformAdmin={isPlatformAdmin}>
      <div className="min-w-0 space-y-5">
        {showNavigation ? (
          <AdminSubNavigation
            ariaLabel={t("adminDashboard.workspaceNavigation.fairUseAriaLabel", "Fair use views")}
            mobileTestId="fair-use-view-select"
            navigationTestId="fair-use-view-navigation"
            options={options}
            value={view}
            mobileLabel={t(
              "adminDashboard.workspaceNavigation.fairUseMobileLabel",
              "Fair use view"
            )}
            onValueChange={(next) => onViewChange(next as FairUseAreaView)}
          />
        ) : null}

        {/* One document behind three of the four tabs, so the "someone else
            saved this" warning belongs to the area, not to whichever tab
            happened to press Save. */}
        <FairUseConfigConflictBanner />

        <div className="min-w-0 space-y-5">
          {view === "drift" ? <FairUseTabDrift /> : null}
          {view === "users" ? <FairUseTabUsers /> : null}
          {view === "leads" ? <FairUseTabLeads /> : null}
          {view === "costs" ? <FairUseTabCosts /> : null}
          {view === "plans" ? <FairUseTabPlans /> : null}
          {view === "settings" ? <FairUseTabSettings /> : null}
        </div>
      </div>
    </FairUseConfigFormProvider>
  )
}
